// src/components/checklist-item-display/ChecklistItemDisplay.js
import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { MoreVert } from '@mui/icons-material';
import { addCardToQueue, addCardToQueueDisplay, updateCardDataQuantity } from '../../store/card/card.action';
import { removeCardFromFirestore } from '../../utils/firebase/firebase.utils';
import { selectCardCount, selectUser } from '../../store/user/user.selector';
import { setIsViewingNewSelection, setNewSelectionCardData } from '../../store/showcase/showcase.action';
import { setSystemNotification } from '../../store/system/system.action';
import { addPSACard } from '../../utils/firebase/psa';
import SetSelectionModal from '../set-selection-modal/SetSelectionModal';
import ListingSelectionModal from '../listing-selection-modal/ListingSelectionModal';
import CardInfoModal from '../../pages/checklist-page/components/checklist-display/components/checklist-item-panel/components/card-info-modal/CardInfoModal';
import { buildEbaySearchUrl as buildEbaySearchUrlUtil } from '../../utils/ebay/buildEbaySearchUrl';
import CardAttribute from '../card-attribute/CardAttribute';
import InputDropdown from '../input-dropdown/InputDropdown';
import addIcon from '../../assets/system/icons/add-icon.png';
import minusIcon from '../../assets/system/icons/minus-icon.png';

// styles
import {
    ChecklistItemDisplayContainer,
    CardDetails,
    TopCardContent,
    LeftCardContent,
    CardNumber,
    QuantityControlsContainer,
    QtyContent,
    CardHeader,
    AddBtn,
    AttributesContainer,
    ParallelConfiguration,
    Parallels,
    Parallel,
    SelectedParallelContainer,
    ParallelDetailsContainer,
    ActionContainer,
    DeselectButton,
    InputContainer,
    InputField,
    VariationRows, // Kept for later use
    VariationRow, // Kept for later use
    CardContentWrapper,
    QtyTicker,
    QtyButton,
    CardNumberInput,
    EllipsisMenuWrapper,
    EllipsisButton,
    EllipsisDropdownMenu,
    EllipsisMenuContent
} from './ChecklistItemDisplay.styles';

export default function ChecklistItemDisplay({
    card,
    parallels = [],
    showcaseView = false,
    cardOpacity = 1,
    selectedParallelFilter = 'All Parallels',
    onCardSelect = null, // Optional callback for custom card selection (e.g., PSA)
    isSelected = false, // Whether this card is selected for batch operations
    onSelectionChange = null // Callback when selection changes: (cardNumber, isSelected) => void
}) {
    // Hooks
    const navigate = useNavigate();
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = useParams();
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const cardCount = useSelector(selectCardCount);
    // Removed queue state subscriptions - actions now get state from store

    // State
    const [parallelWasSelected, setParallelWasSelected] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [showSetModal, setShowSetModal] = useState(false);
    const [showListingModal, setShowListingModal] = useState(false);
    const [showCardInfoModal, setShowCardInfoModal] = useState(false);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [customParallelName, setCustomParallelName] = useState('');
    const [customParallelNumerator, setCustomParallelNumerator] = useState('');
    const [customParallelDenominator, setCustomParallelDenominator] = useState('');
    const [customParallelColor, setCustomParallelColor] = useState('#FFFFFF');
    // State for variation ellipsis menus and remove menus (still used in renderVariationRow)
    const [variationEllipsisMenuOpen, setVariationEllipsisMenuOpen] = useState({});
    const [removeMenuOpen, setRemoveMenuOpen] = useState({});
    const [variationMenuPositions, setVariationMenuPositions] = useState({});
    const [removeMenuPositions, setRemoveMenuPositions] = useState({});
    
    // New state for redesigned controls
    const [selectedParallel, setSelectedParallel] = useState('BASE');
    const [qtyValue, setQtyValue] = useState(1);
    const [cardNumberInput, setCardNumberInput] = useState('');

    // Refs for variation ellipsis menus and remove menus (still used in renderVariationRow)
    const variationEllipsisRefs = useRef({});
    const removeButtonRefs = useRef({});
    const removeMenuRefs = useRef({});

    // Use ref to preserve expanded state when card data updates
    // Store expanded state in ref keyed by cardNumber so it persists across re-renders
    // This is necessary because Redux updates create new card object references
    const expandedStateRef = useRef({});

    // Constants
    const activeQty = 1;
    const { name, cardAttributes, cardNumber, collectedCards, quantity } = card;

    // Initialize state from ref to preserve across re-renders
    const [variationsExpanded, setVariationsExpanded] = useState(() => {
        return expandedStateRef.current[cardNumber] || false;
    });

    // Use useLayoutEffect to synchronously restore expanded state before paint
    // This ensures the state is restored immediately when card data updates
    useLayoutEffect(() => {
        const persistedExpanded = expandedStateRef.current[cardNumber] || false;
        const hasCollectedCards = collectedCards?.length > 0;

        // If card was expanded and still has cards, restore expanded state immediately
        if (hasCollectedCards && persistedExpanded && !variationsExpanded) {
            setVariationsExpanded(true);
        } else if (!hasCollectedCards && variationsExpanded) {
            // If no cards left, collapse
            expandedStateRef.current[cardNumber] = false;
            setVariationsExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNumber, collectedCards?.length]);

    // Sync state with ref when card data changes to preserve expanded state across Redux updates
    // This ensures that when the card object reference changes, we restore the expanded state
    useEffect(() => {
        const currentCollectedCardsLength = collectedCards?.length || 0;
        const wasExpanded = expandedStateRef.current[cardNumber] || false;

        // If card still has collectedCards and was previously expanded, restore expanded state
        if (currentCollectedCardsLength > 0 && wasExpanded && !variationsExpanded) {
            // Card was expanded and still has cards - restore expanded state
            setVariationsExpanded(true);
        } else if (currentCollectedCardsLength === 0 && variationsExpanded) {
            // If no cards left, collapse and clear the ref
            expandedStateRef.current[cardNumber] = false;
            setVariationsExpanded(false);
        } else if (currentCollectedCardsLength > 0 && wasExpanded !== variationsExpanded) {
            // Sync state with ref value (ref is source of truth)
            setVariationsExpanded(wasExpanded);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNumber, collectedCards?.length]);

    // Keep ref in sync with state whenever state changes (state is source of truth for user actions)
    useEffect(() => {
        expandedStateRef.current[cardNumber] = variationsExpanded;
    }, [variationsExpanded, cardNumber]);

    useEffect(() => {
        if (!parallelWasSelected) {
            setInputValue('');
        }
        if (parallelWasSelected !== 'custom') {
            setCustomParallelName('');
            setCustomParallelNumerator('');
            setCustomParallelDenominator('');
            setCustomParallelColor('#FFFFFF');
        }
    }, [parallelWasSelected]);

    // Reset card number input when parallel selection changes
    useEffect(() => {
        setCardNumberInput('');
    }, [selectedParallel]);

    // Helper functions
    const createCardData = (card, collectedCards) => ({
        ...card,
        collectedCards: collectedCards.map(parallel => ({
            cardParallelName: parallel.cardParallelName,
            cardColor: parallel.cardColor,
            cardQty: activeQty,
            cardPrintNumber: parallel.cardPrintNumber,
            cardSupply: parallel.cardSupply
        }))
    });

    const buildEbaySearchUrl = useCallback(
        (card, parallel) => buildEbaySearchUrlUtil(card, parallel, series),
        [series]
    );

    const dispatchCardToQueue = (cardData, qty = 1) => {
        dispatch(addCardToQueue(sport, year, manufacturer, series, cardType, cardTypeCategory, cardData, qty));
        dispatch(addCardToQueueDisplay(sport, year, manufacturer, series, cardType, cardTypeCategory, cardData, qty));
    };

    // Event handlers
    // eslint-disable-next-line no-unused-vars
    const handleAddBaseCardToQueue = () => { // Kept for later use
        const baseCard = createCardData(card, [{
            cardParallelName: 'BASE',
            cardColor: "#FFFFFF",
            cardSupply: null,
            cardPrintNumber: null
        }]);
        dispatchCardToQueue(baseCard);
    };

    const handleAddParallelToQueue = () => {
        setVariationsExpanded(false);
        if (inputValue === "" && parallels[parallelWasSelected].limitedTo) return;

        const selectedParallel = parallels[parallelWasSelected];
        const parallelCard = {
            ...card,
            cardAttributes: [...card.cardAttributes],
            collectedCards: [{
                cardParallelName: selectedParallel.name,
                cardColor: selectedParallel.color,
                cardQty: activeQty,
                cardPrintNumber: selectedParallel.limitedTo ? parseInt(inputValue, 10) : null,
                cardSupply: selectedParallel.limitedTo
            }]
        };

        dispatchCardToQueue(parallelCard);
        setParallelWasSelected(null);
        setInputValue('');
    };

    const handleAddCustomParallelToQueue = () => {
        setVariationsExpanded(false);

        // Validate that name is provided
        if (!customParallelName.trim()) return;

        // If numerator or denominator is provided, both should be provided and valid
        const hasNumerator = customParallelNumerator.trim() !== '';
        const hasDenominator = customParallelDenominator.trim() !== '';

        if (hasNumerator || hasDenominator) {
            // Both must be provided and valid numbers
            const numerator = parseInt(customParallelNumerator, 10);
            const denominator = parseInt(customParallelDenominator, 10);

            if (isNaN(numerator) || isNaN(denominator)) {
                return;
            }
        }

        // Validate hex color format (must be # followed by 3 or 6 hex digits)
        // If empty or invalid, default to #FFFFFF
        const hexColor = customParallelColor.trim();
        const isValidHex = hexColor && (/^#[0-9A-Fa-f]{6}$/.test(hexColor) || /^#[0-9A-Fa-f]{3}$/.test(hexColor));
        const finalColor = isValidHex ? hexColor : '#FFFFFF';

        const customParallelCard = {
            ...card,
            cardAttributes: [...card.cardAttributes],
            collectedCards: [{
                cardParallelName: customParallelName.trim(),
                cardColor: finalColor,
                cardQty: activeQty,
                cardPrintNumber: customParallelNumerator.trim() ? parseInt(customParallelNumerator, 10) : null,
                cardSupply: customParallelDenominator.trim() ? parseInt(customParallelDenominator, 10) : null
            }]
        };

        dispatchCardToQueue(customParallelCard);
        setParallelWasSelected(null);
        setCustomParallelName('');
        setCustomParallelNumerator('');
        setCustomParallelDenominator('');
        setCustomParallelColor('#FFFFFF');
    };

    const handleCustomParallelClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        // Use flushSync to ensure state update is committed immediately
        flushSync(() => {
            setVariationsExpanded(false);
            setParallelWasSelected('custom');
        });
    };

    const handleParallelClick = (e, index) => {
        e.stopPropagation();
        e.preventDefault();
        // Use flushSync to ensure state update is committed immediately
        // This prevents React 18's automatic batching from delaying the update
        // which could cause the useEffect to reset it before it's committed
        flushSync(() => {
            setVariationsExpanded(false);
            setParallelWasSelected(index);
        });
    };

    const handleDeselectParallel = (e) => {
        e.stopPropagation();
        setParallelWasSelected(null);
    };

    // Helper function to get max removable quantity for selected parallel
    const getMaxRemovableQty = useCallback(() => {
        if (selectedParallel === 'BASE') {
            const baseVariation = collectedCards?.find(c => c.cardParallelName === 'BASE');
            return baseVariation?.cardQty || 0;
        } else {
            const parallel = parallels.find(p => p.name === selectedParallel);
            if (!parallel) return 0;

            // If it's a numbered card and cardNumberInput is set, find matching variation
            if (parallel.limitedTo && cardNumberInput) {
                const matchingVariation = collectedCards?.find(
                    c => c.cardParallelName === selectedParallel && 
                         c.cardPrintNumber === parseInt(cardNumberInput, 10)
                );
                return matchingVariation?.cardQty || 0;
            } else {
                // For non-numbered cards, find any variation with this parallel name
                const variation = collectedCards?.find(c => c.cardParallelName === selectedParallel);
                return variation?.cardQty || 0;
            }
        }
    }, [selectedParallel, collectedCards, parallels, cardNumberInput]);

    // Reset qtyValue if it exceeds max removable quantity when parallel or card number changes
    useEffect(() => {
        const maxRemovable = getMaxRemovableQty();
        if (qtyValue < 0 && Math.abs(qtyValue) > maxRemovable) {
            // Clamp to max removable (as negative) or reset to 1 if nothing collected
            setQtyValue(maxRemovable > 0 ? -maxRemovable : 1);
        }
    }, [selectedParallel, cardNumberInput, collectedCards, qtyValue, getMaxRemovableQty]);

    // New handlers for redesigned controls
    const handleQtyChange = (delta) => {
        setQtyValue(prev => {
            const newValue = prev + delta;
            
            // Skip 0: go directly from 1 to -1 (when decreasing) or -1 to 1 (when increasing)
            if (newValue === 0) {
                return delta > 0 ? 1 : -1;
            }
            
            // Limit negative values to max removable quantity
            if (newValue < 0) {
                const maxRemovable = getMaxRemovableQty();
                const absNewValue = Math.abs(newValue);
                if (absNewValue > maxRemovable) {
                    // Clamp to max removable (as negative)
                    return maxRemovable > 0 ? -maxRemovable : -1;
                }
            }
            
            return newValue;
        });
    };

    const handleParallelSelect = (parallelName) => {
        setSelectedParallel(parallelName);
        setCardNumberInput(''); // Reset card number when parallel changes
        
        // If selecting a limitedTo parallel, set qtyValue to 1 (since QTY ticker is replaced)
        const selectedParallelObj = parallelName === 'BASE' 
            ? null 
            : parallels.find(p => p.name === parallelName);
        if (selectedParallelObj?.limitedTo) {
            setQtyValue(1);
        } else {
            // Reset to 1 for non-limitedTo parallels
            setQtyValue(1);
        }
    };

    const handleCardNumberInputChange = (e) => {
        const value = e.target.value;
        if (selectedParallel === 'BASE') {
            setCardNumberInput('');
            return;
        }
        
        // Find the selected parallel to get limitedTo value
        const parallel = parallels.find(p => p.name === selectedParallel);
        if (!parallel || !parallel.limitedTo) {
            setCardNumberInput('');
            return;
        }

        const maxValue = parallel.limitedTo;
        if (/^\d*$/.test(value)) {
            const numericValue = parseInt(value, 10);
            if (numericValue >= 1 && numericValue <= maxValue) {
                setCardNumberInput(value);
                // Reset qtyValue to 1 when card number changes (for numbered cards)
                setQtyValue(1);
            } else if (value === '') {
                setCardNumberInput('');
            }
        }
    };

    const handleAddOrRemove = () => {
        if (qtyValue === 0) return;

        if (qtyValue > 0) {
            // Add card
            if (selectedParallel === 'BASE') {
                const baseCard = createCardData(card, [{
                    cardParallelName: 'BASE',
                    cardColor: "#FFFFFF",
                    cardSupply: null,
                    cardPrintNumber: null
                }]);
                dispatchCardToQueue(baseCard, qtyValue);
            } else {
                const parallel = parallels.find(p => p.name === selectedParallel);
                if (!parallel) return;

                // Validate card number if required
                if (parallel.limitedTo && !cardNumberInput) {
                    return; // Don't add if card number is required but not provided
                }

                const parallelCard = {
                    ...card,
                    cardAttributes: [...card.cardAttributes],
                    collectedCards: [{
                        cardParallelName: parallel.name,
                        cardColor: parallel.color,
                        cardQty: 1,
                        cardPrintNumber: parallel.limitedTo ? parseInt(cardNumberInput, 10) : null,
                        cardSupply: parallel.limitedTo
                    }]
                };
                dispatchCardToQueue(parallelCard, qtyValue);
            }
        } else {
            // Remove card (qtyValue is negative)
            const absQty = Math.abs(qtyValue);
            if (selectedParallel === 'BASE') {
                // Find BASE variation in collectedCards
                const baseVariation = collectedCards?.find(c => c.cardParallelName === 'BASE');
                if (baseVariation) {
                    handleRemoveCard(card, 'BASE', Math.min(absQty, baseVariation.cardQty || 1));
                }
            } else {
                const parallel = parallels.find(p => p.name === selectedParallel);
                if (!parallel) return;

                // If it's a numbered card, need to match the card number
                if (parallel.limitedTo && cardNumberInput) {
                    const matchingVariation = collectedCards?.find(
                        c => c.cardParallelName === selectedParallel && 
                             c.cardPrintNumber === parseInt(cardNumberInput, 10)
                    );
                    if (matchingVariation) {
                        handleRemoveCard(card, selectedParallel, Math.min(absQty, matchingVariation.cardQty || 1));
                    }
                } else {
                    // Find any variation with this parallel name (for non-numbered cards)
                    const variation = collectedCards?.find(c => c.cardParallelName === selectedParallel);
                    if (variation) {
                        handleRemoveCard(card, selectedParallel, Math.min(absQty, variation.cardQty || 1));
                    }
                }
            }
        }

        // Reset after action
        setQtyValue(1);
        setCardNumberInput('');
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        const maxValue = parallels[parallelWasSelected].limitedTo;

        if (/^\d*$/.test(value)) {
            const numericValue = parseInt(value, 10);
            if (numericValue >= 1 && numericValue <= maxValue) {
                setInputValue(value);
            } else if (value === '') {
                setInputValue('');
            }
        }
    };

    const handleCustomParallelNameChange = (e) => {
        setCustomParallelName(e.target.value);
    };

    const handleCustomParallelNumeratorChange = (e) => {
        const value = e.target.value;
        if (/^-?\d*$/.test(value)) {
            setCustomParallelNumerator(value);
        }
    };

    const handleCustomParallelDenominatorChange = (e) => {
        const value = e.target.value;
        if (/^-?\d*$/.test(value)) {
            setCustomParallelDenominator(value);
        }
    };

    const handleCustomParallelColorChange = (e) => {
        const value = e.target.value;
        // Allow hex color format: # followed by 0-6 hex digits
        // Remove # if present to check the hex digits
        const hexPart = value.startsWith('#') ? value.slice(1) : value;

        // Only allow valid hex characters (0-9, A-F, a-f) up to 6 digits
        if (/^[0-9A-Fa-f]{0,6}$/.test(hexPart)) {
            // Always ensure it starts with # if there's any content, empty string if nothing
            setCustomParallelColor(hexPart ? '#' + hexPart : '');
        }
    };

    const handleRemoveCard = async (card, parallel, amount) => {
        try {
            const result = await removeCardFromFirestore(user.uid, parallel, amount, sport, year, manufacturer, series, cardType, cardTypeCategory, cardNumber, cardCount, card);
            if (result?.success) {
                // Update Redux state optimistically
                dispatch(updateCardDataQuantity(cardNumber, parallel, amount));

                const message = amount === 1
                    ? 'Card removed successfully'
                    : `${amount} cards removed successfully`;
                dispatch(setSystemNotification(message));
            }
        } catch (error) {
            dispatch(setSystemNotification('Failed to remove card'));
        }
    };

    const handleClick = () => {
        // Don't show card info modal in showcase view or when onCardSelect is provided (PSA selection)
        if (showcaseView || onCardSelect) {
            return;
        }
        // Show card info modal when clicking the main component
        setShowCardInfoModal(true);
    };

    // eslint-disable-next-line no-unused-vars
    const handleExpandVariationsClick = (e) => { // Kept for later use
        e.stopPropagation();
        if (collectedCards?.length > 0) {
            setVariationsExpanded(prevState => {
                const newState = !prevState;
                expandedStateRef.current[cardNumber] = newState; // Update ref immediately
                return newState;
            });
        }
    };

    const handleShowcaseCardAdd = (variation) => {
        // If onCardSelect is provided, use it instead of showcase navigation
        if (onCardSelect) {
            const cardData = {
                ...card,
                selectedVariation: variation,
                cardPath: [sport, year, manufacturer, series, cardType, cardTypeCategory]
            };
            onCardSelect(cardData);
            return;
        }
        
        // Default showcase behavior
        dispatch(setIsViewingNewSelection(true));
        const newSelectionData = {
            cardAttributes: card.cardAttributes,
            cardNumber: card.cardNumber,
            name: card.name,
            team: card.team,
            cardPath: [sport, year, manufacturer, series, cardType, cardTypeCategory],
            cardParallel: variation
        };
        dispatch(setNewSelectionCardData(newSelectionData));
        navigate("/showcase-add/selection-view");
    };

    // Set modal handlers
    // Checkbox selection handler
    const handleCheckboxChange = (e) => {
        e.stopPropagation();
        // Immediately blur to prevent scroll behavior from focus
        e.target.blur();
        if (onSelectionChange) {
            onSelectionChange(card.cardNumber, e.target.checked);
        }
    };

    // Removed handleAddMenuToggle - add menu is commented out

    const handleRemoveMenuToggle = (e, variationName) => {
        e.stopPropagation();
        const buttonRef = removeButtonRefs.current[`button-${variationName}`];
        if (buttonRef) {
            const rect = buttonRef.getBoundingClientRect();
            const menuHeight = 100; // Approximate menu height
            const viewportHeight = window.innerHeight;
            const margin = 10; // Margin from viewport edges
            const spaceBelow = viewportHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            
            // If not enough space below but enough space above, show above
            const showAbove = spaceBelow < menuHeight && spaceAbove > menuHeight;
            
            // Calculate top position, ensuring it stays within viewport with margin
            let topPosition;
            if (showAbove) {
                topPosition = Math.max(margin, rect.top - menuHeight - 4);
            } else {
                topPosition = Math.min(rect.bottom + 4, viewportHeight - menuHeight - margin);
            }
            
            setRemoveMenuPositions(prev => ({
                ...prev,
                [variationName]: {
                    top: topPosition,
                    right: window.innerWidth - rect.right
                }
            }));
        }
        setRemoveMenuOpen(prev => ({
            ...prev,
            [variationName]: !prev[variationName]
        }));
    };

    // Removed add menu click-outside handler - add menu is commented out

    // Click outside handler for remove menus
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(removeMenuOpen).forEach(variationName => {
                if (removeMenuOpen[variationName]) {
                    const buttonRef = removeButtonRefs.current[`button-${variationName}`];
                    const menuRef = removeMenuRefs.current[`menu-${variationName}`];
                    
                    if (
                        buttonRef &&
                        !buttonRef.contains(event.target) &&
                        menuRef &&
                        !menuRef.contains(event.target)
                    ) {
                        setRemoveMenuOpen(prev => ({
                            ...prev,
                            [variationName]: false
                        }));
                    }
                }
            });
        };

        const hasOpenMenu = Object.values(removeMenuOpen).some(open => open);
        if (hasOpenMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [removeMenuOpen]);

    // Click outside handler for variation ellipsis menus
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(variationEllipsisMenuOpen).forEach(variationName => {
                if (variationEllipsisMenuOpen[variationName]) {
                    const buttonRef = variationEllipsisRefs.current[`button-${variationName}`];
                    const menuRef = variationEllipsisRefs.current[`menu-${variationName}`];

                    if (
                        buttonRef &&
                        !buttonRef.contains(event.target) &&
                        menuRef &&
                        !menuRef.contains(event.target)
                    ) {
                        setVariationEllipsisMenuOpen(prev => ({
                            ...prev,
                            [variationName]: false
                        }));
                    }
                }
            });
        };

        const hasOpenMenu = Object.values(variationEllipsisMenuOpen).some(open => open);
        if (hasOpenMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [variationEllipsisMenuOpen]);

    // Removed handleAddAllToSet - batch operations handled in parent
    
    const handleAddVariationToSet = (variationName) => {
        setVariationEllipsisMenuOpen(prev => ({
            ...prev,
            [variationName]: false
        }));
        setSelectedVariation(variationName);
        setShowSetModal(true);
    };

    const handleSetModalClose = () => {
        setShowSetModal(false);
        setSelectedVariation(null);
    };

    const handleSetModalSuccess = (setIds) => {
        const playerName = name.join(' • ');
        const message = selectedVariation
            ? `Added ${selectedVariation} to ${setIds.length} set(s)`
            : `Added all ${playerName} to ${setIds.length} set(s)`;
        dispatch(setSystemNotification(message));
    };

    // Removed handleAddAllToListing - batch operations handled in parent
    
    const handleAddVariationToListing = (variationName) => {
        setVariationEllipsisMenuOpen(prev => ({
            ...prev,
            [variationName]: false
        }));
        setSelectedVariation(variationName);
        setShowListingModal(true);
    };
    
    const handleVariationEllipsisMenuToggle = (e, variationName) => {
        e.stopPropagation();
        const buttonRef = variationEllipsisRefs.current[`button-${variationName}`];
        if (buttonRef) {
            const rect = buttonRef.getBoundingClientRect();
            const menuHeight = 150; // Approximate menu height
            const viewportHeight = window.innerHeight;
            const margin = 10; // Margin from viewport edges
            const spaceBelow = viewportHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            
            // If not enough space below but enough space above, show above
            const showAbove = spaceBelow < menuHeight && spaceAbove > menuHeight;
            
            // Calculate top position, ensuring it stays within viewport with margin
            let topPosition;
            if (showAbove) {
                topPosition = Math.max(margin, rect.top - menuHeight - 4);
            } else {
                topPosition = Math.min(rect.bottom + 4, viewportHeight - menuHeight - margin);
            }
            
            setVariationMenuPositions(prev => ({
                ...prev,
                [variationName]: {
                    top: topPosition,
                    right: window.innerWidth - rect.right
                }
            }));
        }
        setVariationEllipsisMenuOpen(prev => ({
            ...prev,
            [variationName]: !prev[variationName]
        }));
    };

    const handleVariationLabelForPSA = async (variationName) => {
        setVariationEllipsisMenuOpen(prev => ({
            ...prev,
            [variationName]: false
        }));
        
        if (!user?.uid) {
            dispatch(setSystemNotification('Error: User not authenticated'));
            return;
        }

        try {
            // Find the variation from collectedCards
            const variation = collectedCards?.find(c => c.cardParallelName === variationName);
            if (!variation) {
                dispatch(setSystemNotification('Error: Variation not found'));
                return;
            }

            // Construct card data for PSA with the specific variation
            const cardData = {
                ...card,
                selectedVariation: variation,
                cardPath: [sport, year, manufacturer, series, cardType, cardTypeCategory]
            };

            // Create minimal gradingData for a draft (no status, no grade, no service level)
            const gradingData = {
                type: 'draft' // Special type for draft cards
            };

            await addPSACard(user.uid, cardData, gradingData);
            dispatch(setSystemNotification('Card labeled for PSA!'));
        } catch (error) {
            dispatch(setSystemNotification('Error adding card to PSA. Please try again.'));
        }
    };

    // Removed handleAddVariationToListing - batch operations handled in parent

    const handleListingModalClose = () => {
        setShowListingModal(false);
        setSelectedVariation(null);
    };

    const handleListingModalSuccess = (listingIds) => {
        const playerName = name.join(' • ');
        const message = selectedVariation
            ? `Added ${selectedVariation} to ${listingIds.length} sale(s)`
            : `Added all ${playerName} to ${listingIds.length} sale(s)`;
        dispatch(setSystemNotification(message));
    };

    const getCardDataForSet = () => {
        // Construct the checklist path from route parameters
        const checklistPath = `/checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;

        const isCollected = collectedCards && collectedCards.length > 0;

        const baseCardData = {
            cardNumber,
            name,
            team: card.team || [],
            cardAttributes: cardAttributes || [],
            checklistPath,
            isCollected: isCollected
        };

        if (selectedVariation) {
            const variation = collectedCards?.find(c => c.cardParallelName === selectedVariation);
            if (variation) {
                return {
                    ...baseCardData,
                    variation: {
                        cardParallelName: variation.cardParallelName,
                        cardColor: variation.cardColor,
                        cardQty: variation.cardQty,
                        cardPrintNumber: variation.cardPrintNumber,
                        cardSupply: variation.cardSupply
                    }
                };
            } else {
                // Variation not found (shouldn't happen, but handle gracefully)
                return baseCardData;
            }
        } else {
            return {
                ...baseCardData,
                variations: collectedCards || []
            };
        }
    };

    const getCardDataForListing = () => {
        // Construct the checklist path from route parameters
        const checklistPath = `/checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;

        const isCollected = collectedCards && collectedCards.length > 0;

        const baseCardData = {
            cardNumber,
            name,
            team: card.team || [],
            cardAttributes: cardAttributes || [],
            checklistPath,
            isCollected: isCollected,
            // Always include all variations so the modal can display them all
            variations: collectedCards || [],
            // Include base card quantity if no variations
            quantity: collectedCards && collectedCards.length > 0
                ? collectedCards.reduce((sum, v) => sum + (v.cardQty || 1), 0)
                : (quantity || 1)
        };

        // If a specific variation was selected (from expanded view), mark it as pre-selected
        if (selectedVariation) {
            return {
                ...baseCardData,
                preSelectedVariation: selectedVariation
            };
        } else {
            // From collapsed view - all variations should be pre-selected
            return baseCardData;
        }
    };

    // Render helpers
    const renderCardHeader = () => (
        <CardHeader>
            {name.map((playerName, index) => (
                <span key={index}>
                    <h3>{playerName}</h3>
                    {index < name.length - 1 && ' • '}
                </span>
            ))}
        </CardHeader>
    );

    const renderAttributes = () => (
        <AttributesContainer>
            {cardAttributes?.length > 0 && cardAttributes.map(attribute => (
                <CardAttribute key={attribute} text={attribute} />
            ))}
            {/* Expanded view toggle - kept for later use */}
            {/* {cardAttributes?.length > 0 && collectedCards?.length > 0 && <h4>•</h4>}
            {collectedCards?.length > 0 && (
                <ExpandVariations onClick={handleExpandVariationsClick}>
                    {`${collectedCards?.length} ${collectedCards?.length === 1 ? 'variation' : 'variations'} collected ${variationsExpanded ? '↑' : '↓'}`}
                </ExpandVariations>
            )} */}
        </AttributesContainer>
    );

    const renderQuantityControls = () => {
        // Only show controls when not in showcase view
        if (showcaseView) return null;

        // Find selected parallel to check if it has limitedTo
        const selectedParallelObj = selectedParallel === 'BASE' 
            ? null 
            : parallels.find(p => p.name === selectedParallel);
        const hasLimitedTo = selectedParallelObj?.limitedTo;
        const limitedToValue = selectedParallelObj?.limitedTo;

        // Build parallel options for dropdown
        const parallelOptions = ['BASE', ...parallels.map(p => p.name)];

        // Determine if ADD or REMOVE button should be active
        // If limitedTo parallel: only need cardNumberInput
        // Otherwise: need qtyValue !== 0
        const isAddOrRemoveActive = hasLimitedTo 
            ? cardNumberInput !== '' 
            : qtyValue !== 0;

        return (
            <QuantityControlsContainer>
                <QtyContent>
                    {/* Parallel Selection Dropdown */}
                    <div 
                        style={{ width: '120px', minWidth: '40px' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <InputDropdown
                            id={`parallel-select-${cardNumber}`}
                            value={selectedParallel}
                            onChange={handleParallelSelect}
                            size="compact"
                        >
                            {parallelOptions.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </InputDropdown>
                    </div>

                    {/* Card Number Input for limitedTo parallels */}
                    {hasLimitedTo ? (
                        <>
                            <CardNumberInput
                                type="text"
                                value={cardNumberInput}
                                onChange={handleCardNumberInputChange}
                                placeholder="#"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <h4>/ {limitedToValue}</h4>
                            {/* ADD Button for limitedTo parallels */}
                            <AddBtn 
                                $isActive={isAddOrRemoveActive}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isAddOrRemoveActive) {
                                        handleAddOrRemove();
                                    }
                                }}
                            >
                                <h4>ADD</h4>
                            </AddBtn>
                        </>
                    ) : (
                        /* Combined Qty Ticker and ADD/REMOVE Button */
                        <QtyTicker>
                            {(() => {
                                const maxRemovable = getMaxRemovableQty();
                                const isMinusDisabled = qtyValue < 0 && Math.abs(qtyValue) >= maxRemovable;
                                
                                return (
                                    <>
                                        <QtyButton 
                                            $disabled={isMinusDisabled}
                                            onClick={(e) => { 
                                                if (!isMinusDisabled) {
                                                    e.stopPropagation(); 
                                                    handleQtyChange(-1); 
                                                }
                                            }}
                                        >
                                            <img src={minusIcon} alt="decrease quantity" />
                                        </QtyButton>
                                        <AddBtn 
                                            $isActive={isAddOrRemoveActive}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (isAddOrRemoveActive) {
                                                    handleAddOrRemove();
                                                }
                                            }}
                                        >
                                            <h4>{qtyValue < 0 ? `REMOVE ${Math.abs(qtyValue)}` : `+ ADD ${qtyValue}`}</h4>
                                        </AddBtn>
                                        <QtyButton onClick={(e) => { e.stopPropagation(); handleQtyChange(1); }}>
                                            <img src={addIcon} alt="increase quantity" />
                                        </QtyButton>
                                    </>
                                );
                            })()}
                        </QtyTicker>
                    )}


                    {/* Legacy ADD button menu code - kept for later use */}
                    {/* {shouldShowButtons && (
                        <EllipsisMenuWrapper>
                            <AddBtn
                                ref={addButtonRef}
                                $isActive={true}
                                onClick={handleAddMenuToggle}
                            >
                                <h4>+ ADD</h4>
                            </AddBtn>
                            {addMenuOpen && createPortal(
                                <AnimatePresence>
                                    <EllipsisDropdownMenu
                                        ref={addMenuRef}
                                        className='panel-bg'
                                        style={{
                                            top: `${addMenuPosition.top}px`,
                                            right: `${addMenuPosition.right}px`
                                        }}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 4 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <EllipsisMenuContent>
                                            <div />
                                            <ul>
                                                <motion.li
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        handleAddBaseCardToQueue();
                                                        setAddMenuOpen(false);
                                                    }}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.2, delay: 0 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    style={{
                                                        cursor: "pointer",
                                                        listStyle: "none",
                                                        borderRadius: "8px",
                                                        color: "#FFFFFF",
                                                        padding: "12px 16px",
                                                        transition: "background-color 0.2s ease-in-out",
                                                        userSelect: "none"
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                                >
                                                    + BASE
                                                </motion.li>
                                                <motion.li
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        setAddMenuOpen(false);
                                                    }}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.2, delay: 0.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    style={{
                                                        cursor: "pointer",
                                                        listStyle: "none",
                                                        borderRadius: "8px",
                                                        color: "#FFFFFF",
                                                        padding: "12px 16px",
                                                        transition: "background-color 0.2s ease-in-out",
                                                        userSelect: "none"
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                                >
                                                    + PARALLEL
                                                </motion.li>
                                            </ul>
                                        </EllipsisMenuContent>
                                    </EllipsisDropdownMenu>
                                </AnimatePresence>,
                                document.body
                            )}
                        </EllipsisMenuWrapper>
                    )} */}
                </QtyContent>
            </QuantityControlsContainer>
        );
    };

    const renderVariationRow = (parallel) => (
        <VariationRow key={parallel.cardParallelName}>
            <LeftCardContent style={{ marginLeft: '48px' }}>
                <CardAttribute
                    color={parallel.cardColor}
                    text={parallel.cardPrintNumber ? `${parallel.cardParallelName} (${parallel.cardPrintNumber}/${parallel.cardSupply})` : parallel.cardParallelName}
                    size={'L'}
                />
            </LeftCardContent>
            <QtyContent>
                {!showcaseView && <h4>QTY: {parallel.cardQty}</h4>}

                {!showcaseView && (
                    <EllipsisMenuWrapper>
                        <AddBtn
                            ref={(el) => {
                                if (el) {
                                    removeButtonRefs.current[`button-${parallel.cardParallelName}`] = el;
                                }
                            }}
                            $isActive={true}
                            onClick={(e) => handleRemoveMenuToggle(e, parallel.cardParallelName)}
                        >
                            <h4>REMOVE</h4>
                        </AddBtn>
                        {removeMenuOpen[parallel.cardParallelName] && createPortal(
                            <AnimatePresence>
                                <EllipsisDropdownMenu
                                    ref={(el) => {
                                        if (el) {
                                            removeMenuRefs.current[`menu-${parallel.cardParallelName}`] = el;
                                        }
                                    }}
                                    className='panel-bg'
                                    style={{
                                        top: `${removeMenuPositions[parallel.cardParallelName]?.top || 0}px`,
                                        right: `${removeMenuPositions[parallel.cardParallelName]?.right || 0}px`
                                    }}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <EllipsisMenuContent>
                                        <div />
                                        <ul>
                                            <motion.li
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    handleRemoveCard(card, parallel.cardParallelName, 1);
                                                    setRemoveMenuOpen(prev => ({
                                                        ...prev,
                                                        [parallel.cardParallelName]: false
                                                    }));
                                                }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: 0 }}
                                                whileTap={{ scale: 0.95 }}
                                                style={{
                                                    cursor: "pointer",
                                                    listStyle: "none",
                                                    borderRadius: "8px",
                                                    color: "#FFFFFF",
                                                    padding: "12px 16px",
                                                    transition: "background-color 0.2s ease-in-out",
                                                    userSelect: "none"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                            >
                                                REMOVE ONE
                                            </motion.li>
                                            <motion.li
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    handleRemoveCard(card, parallel.cardParallelName, parallel.cardQty);
                                                    setRemoveMenuOpen(prev => ({
                                                        ...prev,
                                                        [parallel.cardParallelName]: false
                                                    }));
                                                }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: 0.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                style={{
                                                    cursor: "pointer",
                                                    listStyle: "none",
                                                    borderRadius: "8px",
                                                    color: "#FFFFFF",
                                                    padding: "12px 16px",
                                                    transition: "background-color 0.2s ease-in-out",
                                                    userSelect: "none"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                            >
                                                REMOVE ALL
                                            </motion.li>
                                        </ul>
                                    </EllipsisMenuContent>
                                </EllipsisDropdownMenu>
                            </AnimatePresence>,
                            document.body
                        )}
                    </EllipsisMenuWrapper>
                )}

                {!showcaseView && (
                    <EllipsisMenuWrapper>
                        <EllipsisButton
                            ref={(el) => {
                                if (el) {
                                    variationEllipsisRefs.current[`button-${parallel.cardParallelName}`] = el;
                                }
                            }}
                            $isActive={true}
                            onClick={(e) => handleVariationEllipsisMenuToggle(e, parallel.cardParallelName)}
                        >
                            <MoreVert style={{ color: '#FFFFFF', fontSize: '20px' }} />
                        </EllipsisButton>
                        {variationEllipsisMenuOpen[parallel.cardParallelName] && createPortal(
                            <AnimatePresence>
                                <EllipsisDropdownMenu
                                    ref={(el) => {
                                        if (el) {
                                            variationEllipsisRefs.current[`menu-${parallel.cardParallelName}`] = el;
                                        }
                                    }}
                                    className='panel-bg'
                                    style={{
                                        top: `${variationMenuPositions[parallel.cardParallelName]?.top || 0}px`,
                                        right: `${variationMenuPositions[parallel.cardParallelName]?.right || 0}px`
                                    }}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <EllipsisMenuContent>
                                        <div />
                                        <ul>
                                            <motion.li
                                                onClick={(e) => { e.stopPropagation(); handleAddVariationToSet(parallel.cardParallelName); }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: 0 }}
                                                whileTap={{ scale: 0.95 }}
                                                style={{
                                                    cursor: "pointer",
                                                    listStyle: "none",
                                                    borderRadius: "8px",
                                                    color: "#FFFFFF",
                                                    padding: "12px 16px",
                                                    transition: "background-color 0.2s ease-in-out",
                                                    userSelect: "none"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                            >
                                                Add to Set
                                            </motion.li>
                                            <motion.li
                                                onClick={(e) => { e.stopPropagation(); handleAddVariationToListing(parallel.cardParallelName); }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: 0.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                style={{
                                                    cursor: "pointer",
                                                    listStyle: "none",
                                                    borderRadius: "8px",
                                                    color: "#FFFFFF",
                                                    padding: "12px 16px",
                                                    transition: "background-color 0.2s ease-in-out",
                                                    userSelect: "none"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                            >
                                                Add to Sale
                                            </motion.li>
                                            <motion.li
                                                onClick={(e) => { e.stopPropagation(); handleVariationLabelForPSA(parallel.cardParallelName); }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: 0.1 }}
                                                whileTap={{ scale: 0.95 }}
                                                style={{
                                                    cursor: "pointer",
                                                    listStyle: "none",
                                                    borderRadius: "8px",
                                                    color: "#FFFFFF",
                                                    padding: "12px 16px",
                                                    transition: "background-color 0.2s ease-in-out",
                                                    userSelect: "none"
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                            >
                                                Label for PSA
                                            </motion.li>
                                        </ul>
                                    </EllipsisMenuContent>
                                </EllipsisDropdownMenu>
                            </AnimatePresence>,
                            document.body
                        )}
                    </EllipsisMenuWrapper>
                )}

                {showcaseView && (
                    <AddBtn $isActive={true} onClick={() => handleShowcaseCardAdd(parallel)}>
                        <h4>SELECT CARD</h4>
                    </AddBtn>
                )}
            </QtyContent>
        </VariationRow>
    );

    // eslint-disable-next-line no-unused-vars
    const renderVariations = () => ( // Kept for later use
        variationsExpanded && (
            <VariationRows>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", marginBottom: "8px" }} />
                {card.collectedCards?.map(renderVariationRow)}
            </VariationRows>
        )
    );

    // eslint-disable-next-line no-unused-vars
    const renderParallelConfiguration = () => (
            <ParallelConfiguration>
                {parallelWasSelected === null && (
                    <>
                        <Parallel
                            key={'customParallel'}
                            $parallelColor={'#FFFFFF'}
                            style={{ marginBottom: '16px' }}
                            onClick={(e) => handleCustomParallelClick(e)}
                            className={parallelWasSelected === 'custom' ? 'active' : ''}
                        >
                            {'ADD CUSTOM PARALLEL'}
                        </Parallel>
                        <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", marginBottom: "16px" }} />
                    </>
                )}
                {parallelWasSelected === null ? (
                    <Parallels>
                        {parallels.map((parallel, index) => (
                            <Parallel
                                key={parallel.name}
                                $parallelColor={parallel.color}
                                onClick={(e) => handleParallelClick(e, index)}
                                className={index === parallelWasSelected ? 'active' : ''}
                            >
                                {parallel.name}
                            </Parallel>
                        ))}
                    </Parallels>
                ) : parallelWasSelected === 'custom' ? (
                    <SelectedParallelContainer>
                        <ParallelDetailsContainer>
                            <ActionContainer>
                                <DeselectButton onClick={(e) => handleDeselectParallel(e)}>
                                    <img src={addIcon} alt="deselect parallel" style={{ transform: 'rotate(45deg)' }} />
                                </DeselectButton>
                                <InputContainer>
                                    <InputField
                                        type="text"
                                        value={customParallelName}
                                        onChange={handleCustomParallelNameChange}
                                        placeholder="PARALLEL NAME"
                                        style={{ width: '320px' }}
                                    />
                                </InputContainer>
                                <InputContainer>
                                    {/* box to display the custom parallel color */}
                                    <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: customParallelColor }} />
                                    <InputField
                                        type="text"
                                        value={customParallelColor}
                                        onChange={handleCustomParallelColorChange}
                                        placeholder="#FFFFFF"
                                        style={{ width: '100px' }}
                                    />
                                </InputContainer>
                                <InputContainer>
                                    <InputField
                                        type="text"
                                        value={customParallelNumerator}
                                        onChange={handleCustomParallelNumeratorChange}
                                        placeholder="#"
                                    />
                                    <h4>/</h4>
                                    <InputField
                                        type="text"
                                        value={customParallelDenominator}
                                        onChange={handleCustomParallelDenominatorChange}
                                        placeholder="#"
                                    />
                                </InputContainer>
                            </ActionContainer>
                            <AddBtn
                                $isActive={customParallelName.trim() !== '' && (
                                    (customParallelNumerator.trim() === '' && customParallelDenominator.trim() === '') ||
                                    (customParallelNumerator.trim() !== '' && customParallelDenominator.trim() !== '' &&
                                        !isNaN(parseInt(customParallelNumerator, 10)) && !isNaN(parseInt(customParallelDenominator, 10)))
                                )}
                                onClick={handleAddCustomParallelToQueue}
                            >
                                <h4>ADD</h4>
                            </AddBtn>
                        </ParallelDetailsContainer>
                    </SelectedParallelContainer>
                ) : (
                    <SelectedParallelContainer>
                        <ParallelDetailsContainer>
                            <ActionContainer>
                                <DeselectButton onClick={(e) => handleDeselectParallel(e)}>
                                    <img src={addIcon} alt="deselect parallel" style={{ transform: 'rotate(45deg)' }} />
                                </DeselectButton>
                                <Parallel className="active" $parallelColor={parallels[parallelWasSelected].color}>
                                    {parallels[parallelWasSelected].name}
                                </Parallel>
                                {parallels[parallelWasSelected].limitedTo && (
                                    <InputContainer>
                                        <InputField
                                            type="text"
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            placeholder="CARD #"
                                        />
                                        <h4>/ {parallels[parallelWasSelected].limitedTo}</h4>
                                    </InputContainer>
                                )}
                            </ActionContainer>
                            <AddBtn
                                $isActive={inputValue !== '' || !parallels[parallelWasSelected].limitedTo}
                                onClick={handleAddParallelToQueue}
                            >
                                <h4>ADD</h4>
                            </AddBtn>
                        </ParallelDetailsContainer>
                    </SelectedParallelContainer>
                )}
            </ParallelConfiguration>
    );

    return (
        <ChecklistItemDisplayContainer
            $isCollected={collectedCards?.length > 0}
            onClick={handleClick}
            className='panel-bg dropshadow'
        >
            <TopCardContent>
                {/* Checkbox for batch selection - far left */}
                {onSelectionChange && (
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={handleCheckboxChange}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            marginRight: '12px',
                            flexShrink: 0
                        }}
                    />
                )}
                <CardContentWrapper $opacity={cardOpacity}>
                    <LeftCardContent>
                        <CardNumber>
                            <h4>{cardNumber}</h4>
                        </CardNumber>
                        <CardDetails>
                            {renderCardHeader()}
                            {(cardAttributes?.length > 0 || collectedCards?.length > 0) && renderAttributes()}
                        </CardDetails>
                    </LeftCardContent>
                </CardContentWrapper>
                {renderQuantityControls()}
            </TopCardContent>

            {/* Expanded view - kept for later use */}
            {/* {renderVariations()} */}
            {/* {renderParallelConfiguration()} */}

            <SetSelectionModal
                isOpen={showSetModal}
                onClose={handleSetModalClose}
                cardData={getCardDataForSet()}
                onSuccess={handleSetModalSuccess}
            />

            <ListingSelectionModal
                isOpen={showListingModal}
                onClose={handleListingModalClose}
                cardData={getCardDataForListing()}
                onSuccess={handleListingModalSuccess}
            />

            <CardInfoModal
                isOpen={showCardInfoModal}
                onClose={() => setShowCardInfoModal(false)}
                card={card}
                year={year}
                manufacturer={manufacturer}
                series={series}
                buildEbaySearchUrl={buildEbaySearchUrl}
                parallels={parallels}
                sport={sport}
                cardType={cardType}
                cardTypeCategory={cardTypeCategory}
                dispatchCardToQueue={dispatchCardToQueue}
                handleRemoveCard={handleRemoveCard}
                showcaseView={showcaseView}
            />
        </ChecklistItemDisplayContainer>
    );
}